import React, { useState } from 'react';
import type { ReviewResponse } from './types';
import ReviewCard from './ReviewCard';

interface ReviewResponsesProps {
  highRatingReviews: ReviewResponse[];
  lowRatingReviews: ReviewResponse[];
  activeTab: 'high' | 'low';
  onTabChange: (tab: 'high' | 'low') => void;
}

const ReviewResponses: React.FC<ReviewResponsesProps> = ({
  highRatingReviews,
  lowRatingReviews,
  activeTab,
  onTabChange,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const displayedReviews = activeTab === 'high' ? highRatingReviews : lowRatingReviews;

  return (
    <div className="bg-[#212121] rounded-lg border border-[#E0E0E021] h-full flex flex-col">
      {/* Header with collapse button */}
      <div className="flex items-center justify-between p-6 border-b border-[#E0E0E021]">
        <h2 className="text-xl font-medium text-[#EE4423]">Review Responses</h2>
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="text-[#EE4423] hover:text-orange-400 transition-colors"
        >
          <svg
            className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="p-6 flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => onTabChange('high')}
              className={`px-6 cursor-pointer py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'high'
                  ? 'bg-green-600 text-white'
                  : 'bg-[#1a1a1a] text-[#616161] hover:bg-[#333333]'
              }`}
            >
              High Ratings ({highRatingReviews.length})
            </button>
            <button
              onClick={() => onTabChange('low')}
              className={`px-6 cursor-pointer py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'low'
                  ? 'bg-red-600 text-white'
                  : 'bg-[#1a1a1a] text-[#616161] hover:bg-[#333333]'
              }`}
            >
              Low Ratings ({lowRatingReviews.length})
            </button>
          </div>

          {/* Review Cards */}
          <div className="space-y-4 overflow-y-auto flex-1 pr-2">
            {displayedReviews.length > 0 ? (
              displayedReviews.map((review, index) => (
                <ReviewCard key={index} review={review} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No {activeTab === 'high' ? 'high' : 'low'} rating reviews found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewResponses;
